import * as THREE from 'three';

/**
 * ThreeSceneManager
 * 
 * Purpose: Manages direct updates to Three.js InstancedMesh objects, completely
 * bypassing React's state/props system for high-frequency (60fps) transform updates.
 * 
 * Architecture:
 * - Model: Physics worker
 * - ViewModel: This class (receives data, updates view)
 * - View: Three.js InstancedMesh objects
 * 
 * This class is the "ViewModel" layer that separates 60fps rendering data
 * from React's UI update cycle.
 */

interface ParticleData {
    position: [number, number, number];
    rotation: [number, number, number, number]; // quaternion (x, y, z, w)
}

class ThreeSceneManager {
    // Map of species name to their PERSISTENT particle data arrays
    // We REUSE these arrays and copy values into them to avoid memory leaks
    private particleDataBySpecies: Map<string, ParticleData[]> = new Map();
    
    // Debug counters
    private updateCount = 0;
    
    /**
     * Update particle transforms for a specific species.
     * This is called at 60fps from the worker message handler.
     * CRITICAL: We copy data into persistent arrays instead of storing references
     * to avoid memory leaks from postMessage clones.
     * 
     * @param speciesName - The species to update
     * @param particles - Array of particle positions and rotations (cloned from worker)
     */
    updateParticles(speciesName: string, particles: ParticleData[]): void {
        // Get or create persistent array for this species
        let persistentArray = this.particleDataBySpecies.get(speciesName);
        
        if (!persistentArray) {
            // First time - create persistent array with initial capacity
            persistentArray = [];
            this.particleDataBySpecies.set(speciesName, persistentArray);
        }
        
        // Resize persistent array if needed (grows only, never shrinks)
        if (persistentArray.length < particles.length) {
            // Grow the array by creating new objects
            for (let i = persistentArray.length; i < particles.length; i++) {
                persistentArray.push({
                    position: [0, 0, 0],
                    rotation: [0, 0, 0, 1]
                });
            }
        }
        
        // Set the effective length (hide unused capacity)
        persistentArray.length = particles.length;
        
        // Copy values from cloned data into persistent objects
        for (let i = 0; i < particles.length; i++) {
            const src = particles[i];
            const dst = persistentArray[i];
            dst.position[0] = src.position[0];
            dst.position[1] = src.position[1];
            dst.position[2] = src.position[2];
            dst.rotation[0] = src.rotation[0];
            dst.rotation[1] = src.rotation[1];
            dst.rotation[2] = src.rotation[2];
            dst.rotation[3] = src.rotation[3];
        }
        
        this.updateCount++;
        
        // Periodic logging for verification
        if (this.updateCount % 300 === 0) {
            console.log(`[ThreeSceneManager] Updated ${particles.length} particles for ${speciesName} (update #${this.updateCount})`);
        }
    }
    
    /**
     * Get particle data for a specific species.
     * Called from useFrame in InstancedSpeciesMesh.
     * 
     * @param speciesName - The species to get data for
     * @returns Array of particle data, or empty array if no data
     */
    getParticles(speciesName: string): ParticleData[] {
        return this.particleDataBySpecies.get(speciesName) || [];
    }
    
    /**
     * Update all species at once.
     * Called from the main worker message handler.
     */
    updateAllParticles(particlesBySpecies: { [speciesName: string]: ParticleData[] }): void {
        for (const [speciesName, particles] of Object.entries(particlesBySpecies)) {
            this.updateParticles(speciesName, particles);
        }
    }
    
    /**
     * Get debug info about stored particle data.
     */
    getDebugInfo(): { storedSpecies: string[], updateCount: number } {
        return {
            storedSpecies: Array.from(this.particleDataBySpecies.keys()),
            updateCount: this.updateCount
        };
    }
    
    /**
     * Clear all particle data (useful for reset/cleanup).
     */
    clear(): void {
        console.log('[ThreeSceneManager] Clearing all particle data');
        this.particleDataBySpecies.clear();
        this.updateCount = 0;
    }
}

// Export singleton instance
export const threeSceneManager = new ThreeSceneManager();

